// Copyright (c) Bentley Systems
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  checkInternalServerError,
  getParametersFromUrlString,
  getTranslatedStringFromParameters,
  getUrlBasedOnEnv,
  setErrorState,
} from "../../Helpers/helper-functions";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectAccessToken } from "../../app/authorization-slice";
import {
  Button,
  LabeledSelect,
  ProgressRadial,
  Text,
} from "@itwin/itwinui-react";
import { mapDataToModels } from "../../Helpers/map-response";
import { SystemType } from "../CredentialsForm/Models/system-type";
import { IntegPackage } from "../CredentialsForm/Models/integ-package";
import { setConfirmation } from "../../app/confirmation-slice";
import LabeledInputComponent from "../../CustomInputComponents/TextInputComponents/LabeledInputComponent";
import { EfFieldValidationTypeEnum } from "../../Helpers/enumerators";
import { SaveOnPremSystemDto } from "./save-on-prem-system-dto";
import { useToastNotificationService } from "../../Helpers/ToastNotificationService";
import { ConfigManager } from "../../Helpers/config-manager";

export type OnPremSystemFormProps = {
  params?: string;
  iTwinId: string;
  onPremiseId: string;
  ///is open directly from URL or is it open in InformationPanel in elist
  isFromUrl: boolean;
  isStandaloneMode?: boolean;
  closeFrollFunction?: (refreshList: boolean) => void;
  navigateToInformationPanelFunction?: (navFrolUrl: string) => void;
  setInforPanelHeaderFunction?: (header: string) => void;
};

const OnPremSystemForm = (props: OnPremSystemFormProps) => {
  const dispatch = useAppDispatch();
  const { showToast } = useToastNotificationService();
  const { params } = useParams();
  const urlParameters = useMemo(
    () => getParametersFromUrlString(params),
    [params],
  );
  const [isLoading, setLoading] = useState<boolean>(false);
  const [isPropertiesLoading, setIsPropertiesLoading] =
    useState<boolean>(false);

  const parameters =
    props && props.isFromUrl
      ? urlParameters
      : getParametersFromUrlString(props.params);

  const accessToken = useAppSelector(selectAccessToken);
  const [name, setName] = useState<string>();
  const [systemTypes, setSystemTypes] = useState<SystemType[]>([]);
  const [systemType, setSystemType] = useState<SystemType>();
  const [integPackages, setIntegPackages] = useState<IntegPackage[]>([]);
  const [integPackage, setIntegPackage] = useState<IntegPackage>();
  const [systemId, setSystemId] = useState<string>();

  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [isFormDataChanged, setIsFormDataChanged] = useState<boolean>(false);

  const [statuses, setStatuses] = useState<{
    [key: string]: "positive" | "warning" | undefined;
  }>({});

  const handleStatusChange = (
    id: string,
    status: "positive" | "warning" | undefined,
  ) => {
    setStatuses((prevStatuses) => ({
      ...prevStatuses,
      [id]: status,
    }));
  };

  const allInputStatusesValid = Object.values(statuses).every(
    (status) => status !== "warning",
  );

  const setInfoPanelHeader = useCallback(
    (vmHeader: string | undefined) => {
      if (
        props.setInforPanelHeaderFunction !== undefined &&
        vmHeader !== undefined
      ) {
        const header = getTranslatedStringFromParameters(vmHeader, parameters);
        props.setInforPanelHeaderFunction(header as string);
      }
    },
    [parameters, props],
  );

  useEffect(() => {
    const headerStr = "New on premise system";
    setInfoPanelHeader(headerStr);
  }, [setInfoPanelHeader]);

  useMemo(() => {
    if (systemType && name && systemId) {
      setIsFormValid(true);
    } else {
      setIsFormValid(false);
    }
  }, [systemType, name, systemId]);

  const wasFormChanged = useCallback(() => {
    if (props.isStandaloneMode) {
      return !!(name || systemType || systemId);
    }
    return !!(name || systemType || integPackage || systemId);
  }, [name, integPackage, systemId, systemType, props.isStandaloneMode]);

  useMemo(() => {
    const isFormChanged = wasFormChanged();
    setIsFormDataChanged(isFormChanged);
  }, [wasFormChanged]);

  useEffect(() => {
    if (systemType !== undefined) {
      // In standalone mode, skip fetching integration packages
      if (props.isStandaloneMode) {
        setIntegPackage(undefined);
        setSystemId(undefined);
        return;
      }

      setIsPropertiesLoading(true);
      setIntegPackage(undefined);
      setSystemId(undefined);

      const abortController = new AbortController();

      const url = getUrlBasedOnEnv(
        `/api/Structure/Connectors/${systemType.name}/integPackages`,
      );
      const propsRunId = crypto.randomUUID();
      fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: accessToken,
          RunId: propsRunId,
        },
        signal: abortController.signal,
      })
        .then(async function (response) {
          if (!response.ok) {
            setErrorState(response.status, dispatch, response.headers.get("X-Run-Id") ?? propsRunId);
            return;
          }
          return response.json();
        })
        .then(function (data) {
          if (!data) {
            return;
          }
          const intPacks: IntegPackage[] = mapDataToModels<IntegPackage>(
            data.availableIntegPackages,
          );
          setIntegPackages(intPacks);
        })
        .catch((error) => {
          if (error.name !== "AbortError") {
            setErrorState(500, dispatch, propsRunId, true);
          }
        })
        .finally(() => setIsPropertiesLoading(false));

      return () => {
        abortController.abort();
      };
    } else {
      // Reset state when systemType is undefined
      setIntegPackages([]);
      setIsPropertiesLoading(false);
    }
  }, [accessToken, dispatch, systemType, props.isStandaloneMode]);

  const systemOptions = useMemo(
    () =>
      systemTypes.map(
        (system) =>
          ({
            label: system.name,
            value: system,
            icon: (
              <img
                alt="icon"
                src={`data:image/jpeg;base64,${system.icon}`}
                style={{ width: 20, height: 20 }}
              />
            ),
          }),
      ),
    [systemTypes],
  );

  const integPackageOptions = useMemo(
    () =>
      integPackages.map(
        (pack) =>
          ({
            label: pack.integPackageName,
            value: pack,
          }),
      ),
    [integPackages],
  );

  const systemIdsOptions = useMemo(() => {
    if (integPackage) {
      return integPackage?.systemIds?.map(
        (sysId) =>
          ({
            label: sysId,
            value: sysId,
          }),
      );
    } else {
      return [];
    }
  }, [integPackage]);

  useEffect(() => {
    setLoading(true);
    const abortController = new AbortController();

    const url = getUrlBasedOnEnv("/api/Structure/Connectors?withIcons=true");
    const connectorsRunId = crypto.randomUUID();
    fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken,
        RunId: connectorsRunId,
      },
      signal: abortController.signal,
    })
      .then(async function (response) {
        if (!response.ok) {
          setErrorState(response.status, dispatch, response.headers.get("X-Run-Id") ?? connectorsRunId);
          return;
        }
        return response.json();
      })
      .then(function (data) {
        if (!data) {
          return;
        }
        const systems: SystemType[] = mapDataToModels<SystemType>(
          data,
        );
        setSystemTypes(systems);
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          setErrorState(500, dispatch, connectorsRunId, true);
        }
      })
      .finally(() => setLoading(false));

    return () => {
      abortController.abort();
    };
  }, [urlParameters, props.params, accessToken, dispatch]);

  function getSaveJson() {
    const saveOnPremiseDto: SaveOnPremSystemDto = {
      name: name as string,
      systemType: systemType?.name as string,
      systemId: systemId as string,
    };
    const jsonBody = JSON.stringify(saveOnPremiseDto);

    return jsonBody;
  }

  const saveOnPremSystem = async () => {
    setLoading(true);
    const path = `/itwins/${props.iTwinId}/onpremises/${props.onPremiseId}/systems`;
    const saveUrl = ConfigManager.edfsItwinInternalUrl + path;

    const jsonBody = getSaveJson();
    const saveRunId = crypto.randomUUID();
    const fetchResponse = await fetch(saveUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken,
        RunId: saveRunId,
      },
      body: jsonBody,
    }).then(async function (response) {
      checkInternalServerError(response.status, dispatch, saveRunId);
      return response.json();
    });
    setLoading(false);
    if (fetchResponse.error) {
      showToast({
        status: "negative",
        message: `${fetchResponse.error.message} ActivityId="${saveRunId}"`,
      });
    } else {
      showToast({ status: "positive", action: "create" });
      closeFRoll(true);
    }
  };

  const onCancelClick = () => {
    if (isFormDataChanged) {
      dispatch(
        setConfirmation({
          isConfirmationOpen: true,
          onOkAction: () => closeFRoll(false),
          caption: "Discard unsaved changes?",
          text: "Changes you made so far will not be updated",
          okText: "Discard anyway",
          cancelText: "Stay",
        }),
      );
    } else {
      closeFRoll(false);
    }
  };

  const closeFRoll = (refreshList: boolean) => {
    if (props.closeFrollFunction !== undefined) {
      props.closeFrollFunction(refreshList);
    }
  };

  return (
    <div
      style={{ height: "100%", overflowY: "hidden" }}
      className="iuiBackgroundColor"
    >
      {isLoading ? (
        <div
          className="becs-ltr-margin"
          style={{ width: "100%", display: "flex", justifyContent: "center" }}
        >
          <ProgressRadial indeterminate />
        </div>
      ) : (
        <div
          style={{
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {props.isFromUrl && (
            <div className="becs-all-margin">
              <div className="rowWrap">
                <Text as="h3" variant="title">
                  New on premise system
                </Text>
              </div>
            </div>
          )}
          <div
            className="becs-infopanelbody-footer-line"
            style={{
              flex: 1,
              overflowY: "auto",
            }}
          >
            <div className="becs-all-margin">
              <LabeledInputComponent
                required={true}
                validationType={EfFieldValidationTypeEnum.Name200}
                onStatusChange={(status) => handleStatusChange("name", status)}
                displayStyle="default"
                label="User connection name"
                placeholder="Enter text here..."
                maxLength={200}
                key="LbInConnName"
                defaultValue={name}
                onChange={(currentValue) => {
                  setName(currentValue.target.value);
                }}
              />

              <div className="becs-t-margin">
                <LabeledSelect
                  required={true}
                  label={"System type"}
                  options={systemOptions}
                  onChange={(currentValue: SystemType) => {
                    setSystemType(currentValue);
                  }}
                  value={systemType}
                />
              </div>

              {isPropertiesLoading ? (
                <div
                  className="becs-ltr-margin"
                  style={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <ProgressRadial indeterminate />
                </div>
              ) : (
                <div>
                  {/* Only show Integration Package dropdown in traditional mode */}
                  {systemType && !props.isStandaloneMode && (
                    <div className="becs-t-margin">
                      <LabeledSelect
                        required={true}
                        label={"Integration package"}
                        options={integPackageOptions}
                        onChange={(currentValue: IntegPackage) => {
                          setIntegPackage(currentValue);
                        }}
                        value={integPackage}
                      />
                    </div>
                  )}
                  {systemType && (
                    <div>
                      <div className="becs-t-margin">
                        {props.isStandaloneMode ? (
                          <LabeledInputComponent
                            label="System ID"
                            required={true}
                            validationType={EfFieldValidationTypeEnum.SystemId}
                            defaultValue={systemId}
                            onChange={(e) => setSystemId(e.target.value)}
                            onStatusChange={(status) => handleStatusChange("systemId", status)}
                          />
                        ) : (
                          <LabeledSelect
                            required={true}
                            label={"System ID"}
                            options={systemIdsOptions}
                            onChange={(currentValue: string) => {
                              setSystemId(currentValue);
                            }}
                            value={systemId}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <div className="becs-all-margin">
              <div className="rowWrap">
                <Button
                  key="saveCredentialBtn"
                  as="button"
                  onClick={() => { void saveOnPremSystem(); }}
                  styleType="cta"
                  disabled={
                    !(isFormValid && isFormDataChanged && allInputStatusesValid)
                  }
                >
                  <div>Save</div>
                </Button>
                <Button
                  key="cancelCredentialBtn"
                  as="button"
                  onClick={() => onCancelClick()}
                >
                  <div>Cancel</div>
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnPremSystemForm;
